<!-- Shows all BusinessEntities accessible through the role's task responsibilities. -->
<!-- Fetches process elements for each unique process in the role's responsibilities, then -->
<!-- aggregates entity_refs from the tasks that belong to this role. -->
<template>
    <div class="role-entity-refs-section">
        <Title>Accessible Domain Objects</Title>
        <p v-if="!entityRefs.length && !isLoading" class="no-refs">
            No entities found. Entities become accessible when tasks belonging
            to this role's lanes reference business entities.
        </p>
        <DataTable
            v-else
            :value="entityRefs"
            :loading="isLoading"
            class="entity-refs-table"
        >
            <Column field="tasks" header="Via Tasks">
                <template #body="{ data }">
                    <span class="task-links">
                        <RouterLink
                            v-for="task in data.tasks"
                            :key="task.id"
                            :id="`role-task-ref-link-${data.entity_id}-${task.id}`"
                            class="task-link"
                            :to="{
                                name: 'processElementPage',
                                params: {
                                    orgName: route.params.orgName,
                                    stekName: route.params.stekName,
                                    elementId: task.id,
                                    elementType: 'task',
                                },
                            }"
                            >{{ task.name }}</RouterLink
                        >
                    </span>
                </template>
            </Column>
            <Column field="labels" header="Relationship">
                <template #body="{ data }">
                    <span class="relationship-labels">{{ data.labels }}</span>
                </template>
            </Column>
            <Column field="entityName" header="Entity">
                <template #body="{ data }">
                    <RouterLink
                        :id="`role-entity-ref-link-${data.entity_id}`"
                        class="entity-link"
                        :to="{
                            name: 'businessEntityPage',
                            params: {
                                orgName: route.params.orgName,
                                stekName: route.params.stekName,
                                businessEntityId: data.entity_id,
                            },
                        }"
                    >
                        {{ data.entityName }}
                    </RouterLink>
                </template>
            </Column>
        </DataTable>
    </div>
    <template v-for="ref in entityRefs" :key="ref.entity_id">
        <Popup
            v-if="!isEditing && ref.entity_id"
            :anchor="`a#role-entity-ref-link-${ref.entity_id}`"
        >
            <GenericCard
                :entityId="ref.entity_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>
    <!-- Task hover popups -->
    <template v-for="ref in entityRefs" :key="`tasks-${ref.entity_id}`">
        <template v-for="task in ref.tasks" :key="task.id">
            <Popup
                v-if="!isEditing && task.id"
                :anchor="`a#role-task-ref-link-${ref.entity_id}-${task.id}`"
            >
                <GenericCard
                    :entityId="task.id"
                    :entityType="EntityType.TASK"
                    :showPath="true"
                />
            </Popup>
        </template>
    </template>
</template>

<script setup lang="ts">
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useRoleManager } from "@/composables/role-manager";
import { api, type types } from "@/lib/openapi";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";

import Title from "../Title.vue";

const route = useRoute();
const { role } = useRoleManager();
const { isEditing } = useEditManager();
const { entities } = useBusinessEntitiesManager();

/** Raw entity_refs collected from all responsibility tasks, keyed by entity_id. */
interface AggregatedTask {
    id: string;
    name: string;
}

interface AggregatedRef {
    entity_id: string;
    entityName: string;
    labels: string;
    tasks: AggregatedTask[];
}

const rawElementsByProcess = ref<Map<string, types.ProcessElement[]>>(
    new Map()
);
const isLoading = ref(false);

const fetchProcessElements = async (processId: string) => {
    if (rawElementsByProcess.value.has(processId)) return;

    const { data, response } =
        await api.startProcessProcessElementMgtGetProcessElementsByProcessStartPost(
            { body: { process_id: processId } }
        );
    if (response.ok && Array.isArray(data)) {
        rawElementsByProcess.value = new Map(rawElementsByProcess.value).set(
            processId,
            data
        );
    }
};

watch(
    () => role.value?.responsibilities,
    async (responsibilities) => {
        if (!responsibilities?.length) {
            rawElementsByProcess.value = new Map();
            return;
        }

        isLoading.value = true;
        rawElementsByProcess.value = new Map();

        const uniqueProcessIds = [
            ...new Set(responsibilities.map((r) => r.process_id)),
        ];
        await Promise.all(uniqueProcessIds.map(fetchProcessElements));

        isLoading.value = false;
    },
    { immediate: true }
);

const entityRefs = computed<AggregatedRef[]>(() => {
    const responsibilities = role.value?.responsibilities ?? [];
    if (!responsibilities.length) return [];

    // Build a set of responsibility task IDs for quick lookup
    const responsibilityIds = new Set(responsibilities.map((r) => r.identity));

    // For each responsibility task, collect its entity_refs
    const byEntityId = new Map<
        string,
        { labels: Set<string>; tasks: Map<string, string> }
    >();

    for (const [processId, elements] of rawElementsByProcess.value) {
        for (const element of elements) {
            if (!responsibilityIds.has(element.identity)) continue;
            for (const ref of element.entity_refs ?? []) {
                const existing = byEntityId.get(ref.entity_id) ?? {
                    labels: new Set(),
                    tasks: new Map<string, string>(),
                };
                existing.labels.add(ref.label);

                const responsibility = responsibilities.find(
                    (r) =>
                        r.process_id === processId &&
                        r.identity === element.identity
                );
                if (responsibility) {
                    existing.tasks.set(element.identity, responsibility.name);
                }

                byEntityId.set(ref.entity_id, existing);
            }
        }
    }

    return Array.from(byEntityId.entries()).map(([entity_id, agg]) => {
        const entity = entities.value?.find((e) => e.identity === entity_id);
        return {
            entity_id,
            entityName: entity?.name ?? entity_id,
            labels: Array.from(agg.labels).join(", "),
            tasks: Array.from(agg.tasks.entries()).map(([id, name]) => ({
                id,
                name,
            })),
        };
    });
});
</script>

<style scoped lang="scss">
.role-entity-refs-section {
    margin-top: 1rem;
}

.no-refs {
    color: var(--text-color-secondary);
    font-style: italic;
    margin: 1rem 0;
}

.entity-refs-table {
    margin: 1rem 0;
}

.entity-link {
    text-decoration: none;
    color: var(--stekz-primary-color);
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
}

.relationship-labels {
    font-style: italic;
    color: var(--text-color-secondary);
}

.task-links {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.task-link {
    text-decoration: none;
    color: var(--stekz-primary-color);
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
}
</style>
